import { defineStore } from 'pinia';
import RemindersAPI from 'dashboard/api/reminders';

// Reminders — backed by the Rails API (/api/v1/accounts/:id/reminders).
// Firing is server-side (Dotchat::TriggerDueRemindersJob); this store is only
// CRUD + the calendar-event link. View-model uses camelCase + a local
// 'YYYY-MM-DDTHH:mm' triggerAt (what the datetime-local input + list expect).

const pad = n => String(n).padStart(2, '0');
const toLocalInput = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const toViewModel = r => ({
  id: r.id,
  displayId: r.display_id,
  publicId: r.public_id,
  title: r.title,
  description: r.description || '',
  triggerType: r.trigger_type || 'time',
  triggerAt: toLocalInput(r.trigger_at),
  status: r.status,
  firedAt: r.fired_at,
  color: r.color || null,
  sourceEventId: r.calendar_event_id,
  agentIds: r.agent_ids || [],
  agents: r.agents || [],
  contacts: r.contacts || [],
  contactIds: r.contact_ids || [],
  labels: r.labels || [],
});

const toPayload = form => {
  const payload = {
    title: form.title,
    description: form.description,
    trigger_type: form.triggerType || 'time',
    trigger_at: form.triggerAt ? new Date(form.triggerAt).toISOString() : null,
    color: form.color,
    calendar_event_id: form.sourceEventId ?? form.calendar_event_id ?? null,
    agent_ids: form.agentIds || [],
    contact_ids: form.contactIds || (form.contacts || []).map(c => c.id),
    labels: form.labels || [],
  };
  if (form.status) payload.status = form.status;
  return payload;
};

export const useRemindersStore = defineStore('reminders', {
  state: () => ({
    reminders: [],
  }),
  getters: {
    list: state =>
      [...state.reminders].sort((a, b) =>
        (a.triggerAt || '').localeCompare(b.triggerAt || '')
      ),
    getReminder: state => id => state.reminders.find(r => r.id === id),
  },
  actions: {
    async fetch() {
      const { data } = await RemindersAPI.get();
      this.reminders = (data.payload || []).map(toViewModel);
    },
    async create(payload) {
      const { data } = await RemindersAPI.create(toPayload(payload));
      const reminder = toViewModel(data.payload);
      this.reminders = [...this.reminders, reminder];
      return reminder;
    },
    async update(id, patch) {
      const { data } = await RemindersAPI.update(id, toPayload(patch));
      const reminder = toViewModel(data.payload);
      this.reminders = this.reminders.map(r => (r.id === id ? reminder : r));
      return reminder;
    },
    async remove(id) {
      await RemindersAPI.delete(id);
      this.reminders = this.reminders.filter(r => r.id !== id);
    },
    // Delete every reminder that has already fired (status 'fired').
    async clearTriggered() {
      const { data } = await RemindersAPI.clearTriggered();
      this.reminders = this.reminders.filter(r => r.status !== 'fired');
      return data?.count ?? 0;
    },
    async toggleDone(id) {
      const reminder = this.getReminder(id);
      if (!reminder) return;
      await this.update(id, {
        ...reminder,
        status: reminder.status === 'done' ? 'pending' : 'done',
      });
    },
    // Keep the reminder linked to a calendar event in sync (one per event).
    async upsertForEvent(eventId, payload) {
      const existing = this.reminders.find(r => r.sourceEventId === eventId);
      if (existing) {
        await this.update(existing.id, {
          ...payload,
          sourceEventId: eventId,
          status: 'pending',
        });
        return existing.id;
      }
      const created = await this.create({ ...payload, sourceEventId: eventId });
      return created.id;
    },
    async removeForEvent(eventId) {
      const existing = this.reminders.find(r => r.sourceEventId === eventId);
      if (existing) await this.remove(existing.id);
    },
  },
});
